export interface InlineGridTelemetry {
  attemptId: string
  campaignId: string
  sdkBuild: string
  phase: 'start' | 'result'
  durationMs: number
  wasDeferred: boolean
  outcome?: 'ready' | 'fallback'
  reason?: 'timeout' | 'grid_error'
  diagnostics?: {
    handshakeStage: 'none' | 'hello' | 'ready'
    loadMs: number | null
    helloMs: number | null
    readyMs: number | null
    iframeWidth: number
    iframeHeight: number
    iframeConnected: boolean
    tabVisible: boolean
    layoutVisible: boolean | null
    timeoutMs: number
  }
}

const fields = [
  'attemptId',
  'campaignId',
  'sdkBuild',
  'phase',
  'durationMs',
  'wasDeferred',
  'outcome',
  'reason',
  'diagnostics',
]

const isGridDiagnostics = (value: unknown, durationMs: number): boolean => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }
  const diagnostics = value as Record<string, unknown>
  const keys = [
    'handshakeStage',
    'loadMs',
    'helloMs',
    'readyMs',
    'iframeWidth',
    'iframeHeight',
    'iframeConnected',
    'tabVisible',
    'layoutVisible',
    'timeoutMs',
  ]
  if (!Object.keys(diagnostics).every((key) => keys.includes(key))) {
    return false
  }
  const isNonNegativeNumber = (number: unknown): number is number =>
    typeof number === 'number' && Number.isFinite(number) && number >= 0
  if (
    !['loadMs', 'helloMs', 'readyMs'].every(
      (key) =>
        diagnostics[key] === null ||
        (isNonNegativeNumber(diagnostics[key]) && (diagnostics[key] as number) <= durationMs)
    ) ||
    !isNonNegativeNumber(diagnostics.iframeWidth) ||
    !isNonNegativeNumber(diagnostics.iframeHeight) ||
    !isNonNegativeNumber(diagnostics.timeoutMs) ||
    diagnostics.timeoutMs === 0 ||
    typeof diagnostics.iframeConnected !== 'boolean' ||
    typeof diagnostics.tabVisible !== 'boolean' ||
    (diagnostics.layoutVisible !== null && typeof diagnostics.layoutVisible !== 'boolean')
  ) {
    return false
  }

  if (diagnostics.readyMs !== null) {
    return diagnostics.handshakeStage === 'ready'
  }
  if (diagnostics.helloMs !== null) {
    return diagnostics.handshakeStage === 'hello'
  }
  return diagnostics.handshakeStage === 'none'
}

// Keep the allowlist in sync with Whammy's inline-grid-telemetry receiver.
export const isInlineGridTelemetry = (value: unknown): value is InlineGridTelemetry => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false
  }

  const record = value as Record<string, unknown>
  if (
    !Object.keys(record).every((key) => fields.includes(key)) ||
    typeof record.attemptId !== 'string' ||
    !/^[A-Za-z0-9_-]{1,128}$/.test(record.attemptId) ||
    typeof record.campaignId !== 'string' ||
    !/^[0-9]{1,32}$/.test(record.campaignId) ||
    typeof record.sdkBuild !== 'string' ||
    !/^[A-Za-z0-9._-]{1,128}$/.test(record.sdkBuild) ||
    typeof record.durationMs !== 'number' ||
    !Number.isFinite(record.durationMs) ||
    record.durationMs < 0 ||
    typeof record.wasDeferred !== 'boolean'
  ) {
    return false
  }

  if (
    record.diagnostics !== undefined &&
    (record.phase !== 'result' || !isGridDiagnostics(record.diagnostics, record.durationMs))
  ) {
    return false
  }

  if (record.phase === 'start') {
    return record.durationMs === 0 && record.outcome === undefined && record.reason === undefined
  }

  if (record.phase !== 'result') {
    return false
  }
  if (record.outcome === 'ready') {
    return (
      record.reason === undefined &&
      (record.diagnostics === undefined ||
        (record.diagnostics as InlineGridTelemetry['diagnostics'])?.handshakeStage === 'ready')
    )
  }
  return (
    record.outcome === 'fallback' && ['timeout', 'grid_error'].includes(record.reason as string)
  )
}
